import React, { useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import AppShell from '../../components/layout/AppShell';
import TopBar from '../../components/nav/TopBar';
import SideNav from '../../components/nav/SideNav';
import CommandPalette from '../../components/nav/CommandPalette';
import { ProjectAccessProvider } from 'src/features/projects/ProjectAccessContext';
import { useProject } from 'src/features/projects/hooks/useProject';
import { ProjectSettingProvider } from 'src/features/projects/contexts/ProjectSettingContext';
import { useProjectSetting } from 'src/features/projects/hooks/useProjectSetting';
import { useProjectAccess, hasPerm } from 'src/features/projects/hooks/useProjectAccess';

export default function ProjectLayout() {
    const { projectId } = useParams();
    const location = useLocation();
    const stateName = (location.state as { projectName?: string } | null | undefined)?.projectName;

    const { data: projectRes } = useProject(projectId as string);
    const apiName = projectRes?.data?.name;
    const projectName = apiName ?? stateName ?? 'Project';

    const [cmdOpen, setCmdOpen] = useState(false);

    const { data: accessRes, isLoading: accessLoading } = useProjectAccess(projectId);
    const access = accessRes?.data ?? null;

    const canEvEntry = !accessLoading && hasPerm(access?.permissions, 'wbs.ev.entry');

    const isExternalEntryUser =
        !accessLoading &&
        !!access &&
        access.isExternalProjectCompanyUser === true &&
        access.isOwnerCompanyUser !== true &&
        canEvEntry;

    const { data: settingRes, isLoading: settingLoading } = useProjectSetting(projectId, {
        enabled: !!projectId && !accessLoading && !isExternalEntryUser,
    });

    const setting = settingRes?.data ?? null;

    if (accessLoading) {
        return <div className="p-4 text-sm text-gray-500">Loading project access…</div>;
    }

    // 외부 입력 사용자는 내부 프로젝트 레이아웃으로 들어오지 않음
    if (isExternalEntryUser) {
        return <Navigate to={`/projects/${projectId}/wbs/progress-entry`} replace />;
    }

    return (
        <>
            <ProjectAccessProvider access={access} isLoading={accessLoading}>
                <ProjectSettingProvider setting={setting} isLoading={settingLoading}>
                    <AppShell
                        header={
                            <TopBar
                                projectId={projectId}
                                projectName={projectName}
                                onOpenCmd={() => setCmdOpen(true)}
                            />
                        }
                        sidebar={<SideNav projectId={projectId} />}
                    >
                        <Outlet />
                    </AppShell>
                </ProjectSettingProvider>
            </ProjectAccessProvider>

            <CommandPalette projectId={projectId} weekOf={undefined} open={cmdOpen} onClose={() => setCmdOpen(false)} />
        </>
    );
}
